/** @jsxImportSource @emotion/react */

import { FC, useCallback, useEffect, useMemo, useState } from 'react'

import { useApolloClient } from '@apollo/client'
import { TAKE_ALL_ITEMS_COUNT } from 'constants/consts'
import { bulkUpdatePracticeTest, bulkUpdatePracticeTestVariables } from '__generatedTypes__/bulkUpdatePracticeTest'
import {
  findAllPracticeTest,
  findAllPracticeTest_findAllPracticeTest_items as PracticeTest,
  findAllPracticeTestVariables,
} from '__generatedTypes__/findAllPracticeTest'
import { OrderDirection, PracticeTestTypeEnum } from '__generatedTypes__/globalTypes'
import { updatePracticeTest, updatePracticeTestVariables } from '__generatedTypes__/updatePracticeTest'
import {
  findCurrentlyAccessingCount,
  findCurrentlyAccessingCountVariables,
} from '__generatedTypes__/findCurrentlyAccessingCount'
import {
  BULK_UPDATE_PRACTICE_TEST,
  FIND_CURRENTLY_ACCESSING_COUNT,
  GET_LIST_PRACTICE_TEST,
  UPDATE_PRACTICE_TEST,
} from 'queries/practiceTest'
import { PracticeTestsTable } from 'pagesNew/PracticeTests/List/components/PracticeTestsTable'
import { useLink } from 'routes/cmsRoutes'
import { PageLoader } from 'components/PageLoader'
import { toast } from 'utils/toast'
import { COMMON_PTS_CONFIG } from '../consts'

const USED_FOR_DAILY_CARS_ERROR = 'You cannot update this practice test as it is used in MCAT Daily Practice :('

const REFETCH_CURRENT_COUNT_INTERVAL = 60 * 1000 // 1 minute

export const CommonPTs: FC<{ platformId: string; subjectId: string; type: PracticeTestTypeEnum }> = ({
  platformId,
  subjectId,
  type,
}) => {
  const config = useMemo(() => COMMON_PTS_CONFIG[type], [type])
  const { getLink, navigate } = useLink()
  const { query, mutate } = useApolloClient()
  const [practiceTests, setPracticeTests] = useState<PracticeTest[]>([])
  const [submittingPracticeTestId, setSubmittingPracticeTestId] = useState<string | undefined>()
  const [isLoaded, setIsLoaded] = useState(false)
  const [currentlyAccessingCount, setCurrentlyAccessingCount] = useState<Record<string, number>>({})

  const loadPracticeTest = useCallback(async () => {
    const { data } = await query<findAllPracticeTest, findAllPracticeTestVariables>({
      query: GET_LIST_PRACTICE_TEST,
      variables: {
        params: {
          take: TAKE_ALL_ITEMS_COUNT,
          where: {
            subject: {
              id: subjectId,
            },
            type,
          },
          order: {
            order: OrderDirection.ASC,
          },
        },
      },
      fetchPolicy: 'network-only',
    })
    setPracticeTests(data.findAllPracticeTest.items ?? [])
    setIsLoaded(true)
  }, [subjectId, type])

  useEffect(() => {
    setIsLoaded(false)
    loadPracticeTest()
  }, [loadPracticeTest, subjectId, type])

  useEffect(() => {
    if (!config?.dashboardSettings?.showCurrentlyAccessingColumn) {
      return
    }

    const refetchCurrentlyAccessingCount = () =>
      query<findCurrentlyAccessingCount, findCurrentlyAccessingCountVariables>({
        query: FIND_CURRENTLY_ACCESSING_COUNT,
        variables: {
          platformId,
        },
        fetchPolicy: 'network-only',
      })
        .then(({ data }) =>
          data.findCurrentlyAccessingCount.reduce((acc, item) => {
            acc[item.practiceTestId] = item.currentlyAccessing
            return acc
          }, {} as Record<string, number>)
        )
        .catch((error) => {
          console.error('Error:', error)
          return {}
        })
        .then((count) => setCurrentlyAccessingCount(count))

    refetchCurrentlyAccessingCount()
    const intervalId = setInterval(refetchCurrentlyAccessingCount, REFETCH_CURRENT_COUNT_INTERVAL)

    return () => clearInterval(intervalId)
  }, [platformId, !config?.dashboardSettings?.showCurrentlyAccessingColumn])

  const handleIsLiveChange = useCallback(
    async (practiceTest: PracticeTest, isLiveUpd: boolean) => {
      setSubmittingPracticeTestId(practiceTest.id)
      setPracticeTests((prev) =>
        prev.map((currentPracticeTest) => {
          if (practiceTest.id !== currentPracticeTest.id) {
            return currentPracticeTest
          }

          return {
            ...currentPracticeTest,
            isLive: isLiveUpd,
          }
        })
      )

      try {
        await mutate<updatePracticeTest, updatePracticeTestVariables>({
          mutation: UPDATE_PRACTICE_TEST,
          variables: {
            params: {
              id: practiceTest.id,
              isLive: isLiveUpd,
            },
          },
        })
        toast.success(`${config?.titleSingle} updated successfully`)
      } catch (e) {
        if ((e as Error)?.message === USED_FOR_DAILY_CARS_ERROR) {
          toast.error(
            `You cannot disable Live for this ${config?.titleSingle} since it's already used for the current week`
          )
        } else {
          toast.error(`Failed to update ${config?.titleSingle}`)
        }
        await loadPracticeTest()
      }

      setSubmittingPracticeTestId(undefined)
    },
    [loadPracticeTest]
  )

  const handleAddClick = useCallback(() => {
    navigate(config?.createLink ?? '', undefined, {
      searchParams: new URLSearchParams({
        subjectId,
        defaultOrder: String(practiceTests.length),
      }),
    })
  }, [practiceTests.length, subjectId, config?.createLink])

  useEffect(() => {
    if (practiceTests.length > 0) {
      mutate<bulkUpdatePracticeTest, bulkUpdatePracticeTestVariables>({
        mutation: BULK_UPDATE_PRACTICE_TEST,
        variables: {
          params: practiceTests.map(({ id }, order) => ({ id, order })),
        },
      })
    }
  }, [practiceTests.length])

  if (!isLoaded) {
    return <PageLoader />
  }

  if (!config) {
    return null
  }

  return (
    <PracticeTestsTable
      title={config.titleSingle}
      practiceTests={practiceTests}
      submittingPracticeTestId={submittingPracticeTestId}
      alwaysDisplayLiveSwitch
      onAddClick={handleAddClick}
      onIsLiveChange={handleIsLiveChange}
      getEditLink={(id) => getLink(config.editLink, { practiceTestId: id })}
      currentlyAccessingCount={currentlyAccessingCount}
      settings={config.dashboardSettings}
    />
  )
}
